import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { storefrontApi } from '../lib/api'
import type { CartItem, CartQuote } from '../types/commerce'

interface CartContextValue {
  items: CartItem[]
  count: number
  quote: CartQuote | null
  quoteLoading: boolean
  quoteError: string
  isOpen: boolean
  setOpen: (open: boolean) => void
  add: (variantId: string, quantity?: number) => void
  updateQuantity: (variantId: string, quantity: number) => void
  remove: (variantId: string) => void
  clear: () => void
  refreshQuote: () => Promise<void>
}

const CartContext = createContext<CartContextValue | null>(null)
const STORAGE_KEY = 'gkmaison_cart_v2'

const readCart = (): CartItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(item => typeof item?.variantId === 'string' && Number.isInteger(item?.quantity) && item.quantity > 0)
      .map(item => ({ variantId: item.variantId, quantity: item.quantity }))
  } catch {
    return []
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(readCart)
  const [quote, setQuote] = useState<CartQuote | null>(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quoteError, setQuoteError] = useState('')
  const [isOpen, setOpen] = useState(false)

  const refreshQuote = async () => {
    if (!items.length) {
      setQuote(null)
      setQuoteError('')
      return
    }
    setQuoteLoading(true)
    setQuoteError('')
    try {
      const serverQuote = await storefrontApi.quoteCart(items.map(item => ({ variant_id: item.variantId, quantity: item.quantity })))
      setQuote(serverQuote)
    } catch (error) {
      setQuote(null)
      setQuoteError(error instanceof Error ? error.message : 'Não foi possível validar a sacola.')
    } finally {
      setQuoteLoading(false)
    }
  }

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    let cancelled = false
    const validate = async () => {
      if (!items.length) {
        if (!cancelled) { setQuote(null); setQuoteError(''); setQuoteLoading(false) }
        return
      }
      if (!cancelled) { setQuoteLoading(true); setQuoteError('') }
      try {
        const serverQuote = await storefrontApi.quoteCart(items.map(item => ({ variant_id: item.variantId, quantity: item.quantity })))
        if (!cancelled) setQuote(serverQuote)
      } catch (error) {
        if (!cancelled) {
          setQuote(null)
          setQuoteError(error instanceof Error ? error.message : 'Não foi possível validar a sacola.')
        }
      } finally {
        if (!cancelled) setQuoteLoading(false)
      }
    }
    void validate()
    return () => { cancelled = true }
  }, [items])

  const value = useMemo<CartContextValue>(() => ({
    items,
    quote,
    quoteLoading,
    quoteError,
    isOpen,
    setOpen,
    count: items.reduce((sum, item) => sum + item.quantity, 0),
    add: (variantId, quantity = 1) => {
      setItems(current => {
        const found = current.find(x => x.variantId === variantId)
        if (found) return current.map(x => x.variantId === variantId ? { ...x, quantity: x.quantity + Math.max(1, quantity) } : x)
        return [...current, { variantId, quantity: Math.max(1, quantity) }]
      })
      setOpen(true)
    },
    updateQuantity: (variantId, quantity) => {
      if (quantity <= 0) {
        setItems(current => current.filter(x => x.variantId !== variantId))
        return
      }
      setItems(current => current.map(x => x.variantId === variantId ? { ...x, quantity } : x))
    },
    remove: variantId => setItems(current => current.filter(x => x.variantId !== variantId)),
    clear: () => setItems([]),
    refreshQuote
  }), [items, quote, quoteLoading, quoteError, isOpen])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart deve ser usado dentro de CartProvider')
  return ctx
}
